<template>
  <div class="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 border border-blueGray-200 rounded-lg">

    <!-- Header -->
    <div class="rounded-t mb-0 px-4 py-3 border-0">
      <div class="flex flex-wrap items-center justify-between">
        <h3 class="font-semibold text-base text-blueGray-700">Active Carts</h3>
        <span class="text-xs text-blueGray-400">
          {{ (ecomCarts().data || []).length }} cart{{ (ecomCarts().data || []).length !== 1 ? 's' : '' }}
        </span>
      </div>
    </div>

    <div class="block w-full px-4 pb-6">

      <!-- Loading -->
      <div v-if="loaders().getEcomCarts" class="flex items-center justify-center py-12">
        <span class="loader block"></span>
      </div>

      <!-- Empty -->
      <div v-else-if="(ecomCarts().data || []).length === 0" class="flex flex-col items-center justify-center py-12 text-blueGray-300">
        <i class="pi pi-cart-plus text-4xl mb-3"></i>
        <p class="text-sm">No active carts.</p>
      </div>

      <!-- Cart cards -->
      <div v-else class="flex flex-wrap -mx-2">
        <div
          v-for="(cart, i) in (ecomCarts().data || [])"
          :key="i"
          class="w-full lg:w-6/12 px-2 mb-4"
        >
          <div class="rounded-lg border border-blueGray-200 overflow-hidden shadow-sm h-full">

            <!-- Cart header -->
            <div class="flex items-center justify-between px-4 py-3 bg-blueGray-50 border-b border-blueGray-200">
              <div class="flex items-center gap-3">
                <div class="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <span class="text-emerald-700 font-bold text-sm uppercase">
                    {{ (cart.user && cart.user.name) ? cart.user.name.charAt(0) : '?' }}
                  </span>
                </div>
                <div>
                  <p class="text-xs font-semibold text-blueGray-700 leading-tight">
                    {{ (cart.user && cart.user.name) ? cart.user.name : 'Unknown User' }}
                  </p>
                  <p class="text-xs text-blueGray-400 leading-tight">
                    {{ (cart.user && cart.user.email) ? cart.user.email : '' }}
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-2 flex-shrink-0 ml-2">
                <span
                  class="inline-block text-xs font-bold px-2 py-1 rounded-full uppercase"
                  :class="{
                    'bg-emerald-100 text-emerald-700': cart.status === 'FULFILLED',
                    'bg-orange-100 text-orange-600':   cart.status === 'FILLED',
                    'bg-blueGray-100 text-blueGray-500': cart.status === 'CREATED',
                  }"
                >{{ cart.status }}</span>
                <button
                  @click="showInfo(cart)"
                  class="text-blueGray-400 hover:text-emerald-600 transition-colors"
                  title="View details"
                >
                  <i class="fa fa-eye text-xs"></i>
                </button>
              </div>
            </div>

            <!-- Items list -->
            <div class="divide-y divide-blueGray-100">
              <div
                v-for="(item, j) in (cart.items || [])"
                :key="j"
                class="flex items-center gap-3 px-4 py-3"
              >
                <div class="w-12 h-12 rounded overflow-hidden bg-blueGray-100 flex-shrink-0">
                  <img
                    v-if="item.thumbnails && item.thumbnails.length"
                    :src="item.thumbnails[0]"
                    class="w-full h-full object-cover"
                    :alt="item.name"
                  />
                  <div v-else class="w-full h-full flex items-center justify-center text-blueGray-300">
                    <i class="fa fa-image text-sm"></i>
                  </div>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-xs font-semibold text-blueGray-700 truncate">{{ item.name }}</p>
                  <p v-if="item.sub_title" class="text-xs text-blueGray-400 truncate">{{ item.sub_title }}</p>
                  <p class="text-xs text-blueGray-500 mt-0.5">
                    {{ formatter("NGN").format(item.price - (item.discount || 0)) }}
                    <span v-if="item.discount" class="line-through text-blueGray-300 ml-1">
                      {{ formatter("NGN").format(item.price) }}
                    </span>
                  </p>
                </div>
                <div class="text-right flex-shrink-0">
                  <span class="text-xs text-blueGray-400">× {{ item.pivot && item.pivot.quantity ? item.pivot.quantity : 1 }} sqm</span>
                  <p class="text-xs font-semibold text-blueGray-700">
                    {{ formatter("NGN").format((item.price - (item.discount || 0)) * (item.pivot && item.pivot.quantity ? item.pivot.quantity : 1)) }}
                  </p>
                </div>
              </div>
              <div v-if="!(cart.items || []).length" class="px-4 py-3 text-xs text-blueGray-400 italic text-center">
                Cart is empty.
              </div>
            </div>

            <!-- Cart footer -->
            <div class="flex items-center justify-between px-4 py-3 bg-blueGray-50 border-t border-blueGray-200">
              <span class="text-xs text-blueGray-500">
                {{ (cart.items || []).length }} item{{ (cart.items || []).length !== 1 ? 's' : '' }}
                · {{ formatDate(cart.created_at) }}
              </span>
              <span class="text-sm font-bold text-blueGray-700">
                Total: {{ formatter("NGN").format(cartTotal(cart)) }}
              </span>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- Info Modal -->
    <transition-group>
      <transition name="bounce" :duration="{ enter: 500, leave: 800 }" :key="'cart-modal'">
        <div
          v-if="modals.info && selectedCart"
          @click.self="$_close_all_modal"
          style="width: 100%;"
          class="overflow-x-hidden overflow-y-auto fixed inset-0 z-40 outline-none focus:outline-none justify-center items-center flex w-screen h-screen top-0 left-0"
        >
          <div class="relative w-auto z-50">
            <div class="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
              <div class="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
                <h3 class="text-xl font-semibold capitalize">Cart Details</h3>
                <button
                  class="p-1 ml-auto bg-transparent border-0 text-black opacity-5 float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                  @click="toggleModal('info', false)"
                >
                  <span class="bg-transparent text-black opacity-5 h-6 w-6 text-2xl block outline-none focus:outline-none">×</span>
                </button>
              </div>
              <div class="relative p-6 flex-auto" style="max-height: 460px; overflow-y: auto;">
                <recursive-table
                  :data="selectedCart"
                  :exclude="['id', 'user_id', 'order_id']"
                  :copies="['code', 'uuid']"
                  :dates="['created_at', 'updated_at']"
                  :emails="['email']"
                  :tels="['phone']"
                />
              </div>
              <div class="flex items-center justify-end p-4 border-t border-solid border-blueGray-200 rounded-b">
                <button
                  class="text-red-500 background-transparent capitalize px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                  type="button"
                  @click="toggleModal('info', false)"
                >Close</button>
              </div>
            </div>
          </div>
        </div>
      </transition>
      <transition name="bounce" :duration="{ enter: 500, leave: 800 }" :key="'cart-overlay'">
        <div v-if="modals.info" @click="$_close_all_modal" style="width: 100%; opacity: 0.25;" class="opacity-25 fixed inset-0 z-30 h-screen top-0 left-0 bg-black"></div>
      </transition>
    </transition-group>

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { formatter } from '../../utils/formatter.util';
import { modal } from '../../mixins';
import RecursiveTable from '@/components/Tables/RecursiveTable.vue';

export default {
  mixins: [modal],
  components: { RecursiveTable },
  data() {
    return {
      selectedCart: null,
      modals: { info: false },
    };
  },
  methods: {
    ...mapGetters(['ecomCarts', 'loaders']),
    ...mapActions(['getEcomCarts']),

    toggleModal(name, val) { this.modals[name] = val; },

    showInfo(cart) {
      this.selectedCart = cart;
      this.toggleModal('info', true);
    },

    cartTotal(cart) {
      return (cart.items || []).reduce((sum, item) => {
        const qty = (item.pivot && item.pivot.quantity) ? item.pivot.quantity : 1;
        return sum + (item.price - (item.discount || 0)) * qty;
      }, 0);
    },

    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
      });
    },
  },
  mounted() {
    this.getEcomCarts();
  },
  created() {
    this.formatter = formatter;
  },
};
</script>
